import type { SupabaseClient } from "@supabase/supabase-js";

export type MaterialVerificationStatus =
  | "missing_material"
  | "unverified"
  | "verified_backup"
  | "verified_primary"
  | "verified_both";

export type MaterialScanMethod = "QR" | "NFC" | null;

export interface CheckpointMaterialVerificationRow {
  routeCheckpointId: string;
  checkpointId: string;
  checkpointName: string;
  routeName: string | null;
  siteName: string | null;
  isRequired: boolean;
  activeCheckpointMaterial: number;
  hasCheckpointMaterial: boolean;
  latestMaterialCreatedAt: string | null;
  lastNfcScanAt: string | null;
  lastQrScanAt: string | null;
  lastMaterialScanAt: string | null;
  lastMaterialScanMethod: MaterialScanMethod;
  verificationStatus: MaterialVerificationStatus;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function asInt(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? Math.trunc(value) : 0;
}

function asMaterialScanMethod(value: unknown): MaterialScanMethod {
  return value === "NFC" || value === "QR" ? value : null;
}

function asVerificationStatus(value: unknown): MaterialVerificationStatus {
  switch (value) {
    case "missing_material":
    case "unverified":
    case "verified_backup":
    case "verified_primary":
    case "verified_both":
      return value;
    default:
      return "unverified";
  }
}

export const CHECKPOINT_MATERIAL_VERIFICATION_COLUMNS =
  "route_checkpoint_id, checkpoint_id, checkpoint_name, route_name, site_name, is_required, active_checkpoint_material_count, latest_material_created_at, last_nfc_scan_at, last_qr_scan_at, last_material_scan_at, last_material_scan_method, verification_status";

export function mapCheckpointMaterialVerificationRow(
  raw: Record<string, unknown>,
): CheckpointMaterialVerificationRow {
  const activeCheckpointMaterial = asInt(raw.active_checkpoint_material_count);
  return {
    routeCheckpointId: asString(raw.route_checkpoint_id) ?? "",
    checkpointId: asString(raw.checkpoint_id) ?? "",
    checkpointName: asString(raw.checkpoint_name) ?? "Checkpoint",
    routeName: asString(raw.route_name),
    siteName: asString(raw.site_name),
    isRequired: raw.is_required === true,
    activeCheckpointMaterial,
    hasCheckpointMaterial: activeCheckpointMaterial > 0,
    latestMaterialCreatedAt: asString(raw.latest_material_created_at),
    lastNfcScanAt: asString(raw.last_nfc_scan_at),
    lastQrScanAt: asString(raw.last_qr_scan_at),
    lastMaterialScanAt: asString(raw.last_material_scan_at),
    lastMaterialScanMethod: asMaterialScanMethod(raw.last_material_scan_method),
    verificationStatus: asVerificationStatus(raw.verification_status),
  };
}

export function materialVerificationLabel(status: MaterialVerificationStatus): string {
  switch (status) {
    case "missing_material":
      return "Missing material";
    case "verified_backup":
      return "QR backup verified";
    case "verified_primary":
      return "NFC verified";
    case "verified_both":
      return "NFC + QR verified";
    case "unverified":
    default:
      return "Needs field scan";
  }
}

export function materialVerificationAction(status: MaterialVerificationStatus): string {
  switch (status) {
    case "missing_material":
      return "Create checkpoint material";
    case "verified_backup":
      return "Tap NFC to verify primary";
    case "verified_primary":
      return "Optional: scan QR backup";
    case "verified_both":
      return "Ready";
    case "unverified":
    default:
      return "Tap NFC tag on site; QR is backup";
  }
}

const CHECKPOINT_MATERIAL_VERIFICATION_LIMIT = 2000;

export async function fetchCheckpointMaterialVerification(
  client: SupabaseClient,
  accountId: string,
): Promise<CheckpointMaterialVerificationRow[]> {
  const { data, error } = await client
    .from("management_checkpoint_material_verification")
    .select(CHECKPOINT_MATERIAL_VERIFICATION_COLUMNS)
    .eq("account_id", accountId)
    .limit(CHECKPOINT_MATERIAL_VERIFICATION_LIMIT);
  if (error !== null) {
    throw new Error("Could not load checkpoint material verification.");
  }
  if (!Array.isArray(data)) {
    return [];
  }
  return data.map((row) => mapCheckpointMaterialVerificationRow(row as Record<string, unknown>));
}
